// afiliasi.repo — poin pemberian (bonus rekomendasi) + tautan siapa-merekomendasikan-siapa.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';

export interface Grant {
  id: number; customer_id: number; poin: number; jenis: string; alasan: string;
  ticket_no: string | null; referred_id: number | null; berlaku_bulan: number;
  tgl: string; by_user: string | null;
  ditarik_at: string | null; ditarik_by: string | null; ditarik_alasan: string | null;
}

const KOL = 'id,customer_id,poin,jenis,alasan,ticket_no,referred_id,berlaku_bulan,tgl,by_user,ditarik_at,ditarik_by,ditarik_alasan';

/** Semua pemberian poin satu pelanggan (termasuk yang sudah ditarik — riwayat tak disembunyikan). */
export async function grantsFor(customerId: number): Promise<Grant[]> {
  const { data, error } = await supabase().from('point_grants').select(KOL)
    .eq('customer_id', customerId).order('tgl', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Grant[];
}

/** Pemberian AKTIF semua pelanggan (untuk kolom poin di CRM) → Map<customer_id, Grant[]>. */
export async function grantsAktifSemua(): Promise<Map<number, Grant[]>> {
  const data = await fetchAllPaged<Grant>((a, b) =>
    supabase().from('point_grants').select(KOL).is('ditarik_at', null).order('id').range(a, b));
  const m = new Map<number, Grant[]>();
  for (const g of data) m.set(g.customer_id, [...(m.get(g.customer_id) ?? []), g]);
  return m;
}

export async function beriBonus(d: {
  customer_id: number; poin: number; alasan: string; ticket_no: string | null;
  referred_id: number | null; berlaku_bulan: number; by_user: string;
}): Promise<void> {
  const { error } = await supabase().from('point_grants').insert({ ...d, jenis: 'afiliasi' });
  // Bentrok indeks unik = tiket ini sudah pernah memberi bonus. Itu justru pengaman yang
  // bekerja, bukan kegagalan — jangan tampilkan sebagai error ke petugas.
  if (error && !/duplicate key|uq_grant_tiket/i.test(error.message)) throw new Error(error.message);
}

/** Tarik bonus (servis di-refund/dibatalkan). Alasan WAJIB — dijaga juga oleh basis data. */
export async function tarikBonus(id: number, alasan: string, by: string): Promise<void> {
  if (!alasan.trim()) throw new Error('Alasan penarikan wajib diisi.');
  const { error } = await supabase().from('point_grants')
    .update({ ditarik_at: new Date().toISOString(), ditarik_by: by, ditarik_alasan: alasan.trim() })
    .eq('id', id).is('ditarik_at', null);
  if (error) throw new Error(error.message);
}

/** Bonus afiliasi yang sudah diterima seseorang pada bulan `ym` (yyyy-mm) — untuk batas per bulan. */
export async function bonusBulanIni(customerId: number, ym: string): Promise<number> {
  const { count, error } = await supabase().from('point_grants').select('id', { count: 'exact', head: true })
    .eq('customer_id', customerId).eq('jenis', 'afiliasi').is('ditarik_at', null)
    .gte('tgl', `${ym}-01`).lte('tgl', `${ym}-31`);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/** Bonus yang lahir dari tiket tertentu (untuk ditarik saat tiketnya di-refund). */
export async function grantByTiket(no: string): Promise<Grant | null> {
  const { data, error } = await supabase().from('point_grants').select(KOL)
    .eq('ticket_no', no).eq('jenis', 'afiliasi').is('ditarik_at', null).maybeSingle();
  if (error) throw new Error(error.message);
  return (data ?? null) as Grant | null;
}

export interface RefInfo { id: number; nama: string; wa: string | null; referrer_id: number | null }

/** Data pelanggan + perekomendasinya (dipakai saat memutuskan bonus). */
export async function pelangganRef(id: number): Promise<RefInfo | null> {
  const { data, error } = await supabase().from('customers').select('id,nama,wa,referrer_id').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return (data ?? null) as RefInfo | null;
}

export async function setPerekomendasi(customerId: number, referrerId: number | null): Promise<void> {
  const { error } = await supabase().from('customers').update({ referrer_id: referrerId }).eq('id', customerId);
  if (error) throw new Error(error.message);
}

/** Daftar "siapa mengajak siapa" untuk laporan CRM. */
export async function daftarRekomendasi(): Promise<{ id: number; nama: string; referrer_id: number | null }[]> {
  return await fetchAllPaged<{ id: number; nama: string; referrer_id: number | null }>((a, b) =>
    supabase().from('customers').select('id,nama,referrer_id').not('referrer_id', 'is', null).order('id').range(a, b));
}
